/**
 * GENERATED by scripts/sync-theme-css.mjs. Do not edit.
 *
 * Source: src/app/theme-contract.json (cuts section) + src/app/dcyfr-theme-obsidian.css
 * (dcyfr-labs/dcyfr-labs-registry@2d2987fdc846).
 *
 * The cut axis of the obsidian identity: which `data-cut` values <html> may
 * carry, which one renders when the attribute is absent, a swatch per cut
 * for the chooser and the /design gallery, and the variables each cut
 * resolves to in each scheme. Each swatch is that cut's light-scheme
 * --accent-600 (the filled-control step) as the sRGB fallback the package
 * publishes, so it paints the same wherever it is used and does not depend
 * on which cut is currently stamped.
 *
 * cutVars holds, per cut and scheme, every variable the contract allows a
 * cut to re-author (cuts.overlayGroups plus cuts.overlayExtras), resolved
 * through the package's cascade the way <html> would compute them under
 * that stamp. The /design gallery applies a cut's set as inline custom
 * properties on one card, so every cut renders side by side while <html>
 * carries the visitor's own cut. Nothing in the gallery stamps
 * `data-cut`: the dark overlay selector is compound on <html>, so a stamp
 * on any other element would silently degrade to the base cut, and a card
 * that depended on the live stamp could show only one cut at a time.
 * Values are the sRGB main-context declarations; the @supports wide-gamut
 * overlays are not modelled here.
 *
 * Data only. Colour strings here are data for a swatch or a gallery card,
 * not a hardcoded colour in authored UI; the ESLint colour rule looks at
 * Tailwind class names, which this module has none of.
 */

export const themeCuts = {
  identity: 'obsidian',
  attribute: 'data-cut',
  base: 'black',
  cuts: ['black', 'red', 'blue', 'green', 'rainbow'],
  swatches: {
    black: '#6b6c6e',
    red: '#9f5344',
    blue: '#5a6b91',
    green: '#5a745b',
    rainbow: '#906100',
  },
} as const;

export type ThemeCut = (typeof themeCuts.cuts)[number];

export type CutScheme = 'light' | 'dark';

/** The variables a cut may re-author, in contract order. */
export const cutTokens = ['--primary', '--primary-foreground', '--accent', '--accent-foreground', '--accent-400', '--accent-500', '--accent-600', '--accent-700', '--field', '--field-foreground', '--field-accent', '--sheen', '--border', '--muted', '--muted-foreground'] as const;

export type CutToken = (typeof cutTokens)[number];

/** Per cut and scheme, what each of cutTokens resolves to under that stamp. */
export const cutVars = {
  black: {
    light: {
      '--primary': '#08090e',
      '--primary-foreground': '#f5efe9',
      '--accent': '#e9e2db',
      '--accent-foreground': '#08090e',
      '--accent-400': '#b0b1b4',
      '--accent-500': '#919295',
      '--accent-600': '#6b6c6e',
      '--accent-700': '#545558',
      '--field': '#ece9e5',
      '--field-foreground': '#08090e',
      '--field-accent': '#816a52',
      '--sheen': 'none',
      '--border': '#dacfc3',
      '--muted': '#e9e2db',
      '--muted-foreground': '#605a54',
    },
    dark: {
      '--primary': '#ebe7e4',
      '--primary-foreground': '#08090e',
      '--accent': '#1c1e23',
      '--accent-foreground': '#ebe7e4',
      '--accent-400': '#b0b1b4',
      '--accent-500': '#919295',
      '--accent-600': '#6b6c6e',
      '--accent-700': '#545558',
      '--field': '#08090e',
      '--field-foreground': '#e2ddd8',
      '--field-accent': '#816a52',
      '--sheen': 'none',
      '--border': '#33291e',
      '--muted': '#1c1e23',
      '--muted-foreground': '#a39d98',
    },
  },
  red: {
    light: {
      '--primary': '#08090e',
      '--primary-foreground': '#f5efe9',
      '--accent': '#e9e2db',
      '--accent-foreground': '#08090e',
      '--accent-400': '#eb9887',
      '--accent-500': '#c97a69',
      '--accent-600': '#9f5344',
      '--accent-700': '#853c2e',
      '--field': '#fce3de',
      '--field-foreground': '#08090e',
      '--field-accent': '#9f5344',
      '--sheen': 'none',
      '--border': '#eec8c0',
      '--muted': '#e9e2db',
      '--muted-foreground': '#605a54',
    },
    dark: {
      '--primary': '#ebe7e4',
      '--primary-foreground': '#08090e',
      '--accent': '#1c1e23',
      '--accent-foreground': '#ebe7e4',
      '--accent-400': '#eb9887',
      '--accent-500': '#c97a69',
      '--accent-600': '#9f5344',
      '--accent-700': '#853c2e',
      '--field': '#150705',
      '--field-foreground': '#e2ddd8',
      '--field-accent': '#eb9887',
      '--sheen': 'radial-gradient( ellipse 45% 90px at 50% 0%, #d9563de6 0%, #d9563d73 20%, #d9563d29 48%, #d9563d0a 76%, #d9563d00 100% ), radial-gradient(ellipse 30% 110% at 50% 0%, #d9563d38 0%, #d9563d14 32%, #d9563d00 60%)',
      '--border': '#42221c',
      '--muted': '#1c1e23',
      '--muted-foreground': '#a39d98',
    },
  },
  blue: {
    light: {
      '--primary': '#08090e',
      '--primary-foreground': '#f5efe9',
      '--accent': '#e9e2db',
      '--accent-foreground': '#08090e',
      '--accent-400': '#9eb1da',
      '--accent-500': '#8092ba',
      '--accent-600': '#5a6b91',
      '--accent-700': '#445479',
      '--field': '#e1eafc',
      '--field-foreground': '#08090e',
      '--field-accent': '#5a6b91',
      '--sheen': 'none',
      '--border': '#c3d1f0',
      '--muted': '#e9e2db',
      '--muted-foreground': '#605a54',
    },
    dark: {
      '--primary': '#ebe7e4',
      '--primary-foreground': '#08090e',
      '--accent': '#1c1e23',
      '--accent-foreground': '#ebe7e4',
      '--accent-400': '#9eb1da',
      '--accent-500': '#8092ba',
      '--accent-600': '#5a6b91',
      '--accent-700': '#445479',
      '--field': '#060b16',
      '--field-foreground': '#e2ddd8',
      '--field-accent': '#9eb1da',
      '--sheen': 'radial-gradient( ellipse 45% 90px at 50% 0%, #547febe6 0%, #547feb73 20%, #547feb29 48%, #547feb0a 76%, #547feb00 100% ), radial-gradient(ellipse 30% 110% at 50% 0%, #547feb38 0%, #547feb14 32%, #547feb00 60%)',
      '--border': '#1f2a44',
      '--muted': '#1c1e23',
      '--muted-foreground': '#a39d98',
    },
  },
  green: {
    light: {
      '--primary': '#08090e',
      '--primary-foreground': '#f5efe9',
      '--accent': '#e9e2db',
      '--accent-foreground': '#08090e',
      '--accent-400': '#9eba9f',
      '--accent-500': '#809b81',
      '--accent-600': '#5a745b',
      '--accent-700': '#445d45',
      '--field': '#ddf0dd',
      '--field-foreground': '#08090e',
      '--field-accent': '#5a745b',
      '--sheen': 'none',
      '--border': '#bdd8be',
      '--muted': '#e9e2db',
      '--muted-foreground': '#605a54',
    },
    dark: {
      '--primary': '#ebe7e4',
      '--primary-foreground': '#08090e',
      '--accent': '#1c1e23',
      '--accent-foreground': '#ebe7e4',
      '--accent-400': '#9eba9f',
      '--accent-500': '#809b81',
      '--accent-600': '#5a745b',
      '--accent-700': '#445d45',
      '--field': '#050e05',
      '--field-foreground': '#e2ddd8',
      '--field-accent': '#9eba9f',
      '--sheen': 'radial-gradient( ellipse 45% 90px at 50% 0%, #2aa03fe6 0%, #2aa03f73 20%, #2aa03f29 48%, #2aa03f0a 76%, #2aa03f00 100% ), radial-gradient(ellipse 30% 110% at 50% 0%, #2aa03f38 0%, #2aa03f14 32%, #2aa03f00 60%)',
      '--border': '#18301a',
      '--muted': '#1c1e23',
      '--muted-foreground': '#a39d98',
    },
  },
  rainbow: {
    light: {
      '--primary': '#08090e',
      '--primary-foreground': '#f5efe9',
      '--accent': '#e9e2db',
      '--accent-foreground': '#08090e',
      '--accent-400': '#e7a224',
      '--accent-500': '#c28400',
      '--accent-600': '#906100',
      '--accent-700': '#734d00',
      '--field': '#f6e7d2',
      '--field-foreground': '#08090e',
      '--field-accent': '#906100',
      '--sheen': 'linear-gradient( 100deg, transparent 42%, #d14186 45%, #da9508 48.5%, #73a434 52%, #7d5cc7 55.5%, transparent 59% )',
      '--border': '#e2ceb1',
      '--muted': '#e9e2db',
      '--muted-foreground': '#605a54',
    },
    dark: {
      '--primary': '#ebe7e4',
      '--primary-foreground': '#08090e',
      '--accent': '#1c1e23',
      '--accent-foreground': '#ebe7e4',
      '--accent-400': '#e7a224',
      '--accent-500': '#c28400',
      '--accent-600': '#906100',
      '--accent-700': '#734d00',
      '--field': '#110901',
      '--field-foreground': '#e2ddd8',
      '--field-accent': '#e7a224',
      '--sheen': 'radial-gradient(ellipse 15% 80px at 38% 0%, #ec79a9cc 0%, #ec79a966 22%, #ec79a921 50%, #ec79a908 75%, #ec79a900 100%), radial-gradient(ellipse 15% 80px at 46% 0%, #da9508cc 0%, #da950866 22%, #da950821 50%, #da950808 75%, #da950800 100%), radial-gradient(ellipse 15% 80px at 54% 0%, #8db541cc 0%, #8db54166 22%, #8db54121 50%, #8db54108 75%, #8db54100 100%), radial-gradient(ellipse 15% 80px at 62% 0%, #ad8ff7cc 0%, #ad8ff766 22%, #ad8ff721 50%, #ad8ff708 75%, #ad8ff700 100%), linear-gradient( 100deg, transparent 38%, #e573a333 44%, #d38f0033 48.5%, #87ae3a33 53%, #a789f133 57.5%, transparent 64% )',
      '--border': '#3a270b',
      '--muted': '#1c1e23',
      '--muted-foreground': '#a39d98',
    },
  },
} as const satisfies Record<ThemeCut, Record<CutScheme, Record<CutToken, string>>>;

/** True for a declared cut name; anything else renders the base cut. */
export function isThemeCut(value: unknown): value is ThemeCut {
  return typeof value === 'string' && (themeCuts.cuts as readonly string[]).includes(value);
}
